// PRD 13 — Notice Management page entry. PRD 13 §1.2 / §7 permission matrix:
//
//   Action                | NO_PERMISSION | READ | WRITE | Root
//   ----------------------|:-------------:|:----:|:-----:|:----:
//   LNB entry visible     |       ✗       |  ✓   |   ✓   |  ✓    AdminNavigation.ts permission tag
//   View list / detail    |       ✗       |  ✓   |   ✓   |  ✓    useRequirePermission (READ)
//   Register / edit / draft|      —       |  ✗   |   ✓   |  ✓    WRITE-gated controls
//   Delete                |       —       |  ✗   |   ✗   |  ✓    isRoot-gated (no PUBLICATION:DELETE level)
//
// The notice form is a full-page swap (prototype parity — not a slide panel):
// `view` from the page store decides whether the list or the create/edit form
// renders.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';
import { NoticeFormPanel } from '@/features/notices/components/NoticeFormPanel';
import { NoticeManagementView } from '@/features/notices/components/NoticeManagementView';
import { useNoticesPageStore } from '@/features/notices/store/notices-page.store';

export function NoticeManagementPage() {
  const { t } = useTranslation('notices');
  const canRead = useRequirePermission('PUBLICATION', 'READ');
  // SHCT-582 — PRD 21 §7.4 P-2: `disabled` + 툴팁 rather than a hidden action, so a Role without
  // WRITE can still see what exists and why it is refused.
  const { canWrite, denyProps } = useWriteGuard('PUBLICATION');
  const view = useNoticesPageStore((s) => s.view);
  const openCreate = useNoticesPageStore((s) => s.openCreate);
  const closeForm = useNoticesPageStore((s) => s.closeForm);

  // The page store is module-level, so a form view left open before navigating
  // away would otherwise persist on re-entry. Always land on the list.
  useEffect(() => {
    closeForm();
  }, [closeForm]);

  if (!canRead) return null;

  if (view !== null) return <NoticeFormPanel />;

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('title')}</h1>
        </div>
        <div className="content__actions">
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={openCreate}
            disabled={!canWrite}
            {...denyProps}
          >
            <Plus size={16} />
            {t('addButton')}
          </button>
        </div>
      </div>
      <div className="content__body">
        <NoticeManagementView />
      </div>
    </>
  );
}
